import * as Constants from "common/constants";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import Loader from "../components/common/custom-component/Loader";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer";

import { CalendarToday as CalendarIcon } from "@mui/icons-material";
import { Alert, Box, MenuItem, TextField, Typography, Divider } from "@mui/material";

import * as em from "common/ErrorMessages";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../components/common/custom-component/ToastProvider";

import db from "indexedDB";

import { fetchPendingTasksRequest } from "../store/slices/workflows/pendingTasksSlice";
import { fetchFormByID } from "../store/thunks/formThunks";
import { fetchDatabaseRecord, patchDatabaseRecord } from "../store/thunks/mdsThunks";
import { deletePendingTaskThunk, unclaimPendingTaskThunk } from "../store/thunks/workflowThunks";

import SearchFilterBar from "../components/common/SearchFilterBar";
import { useSearchFilter } from "../hooks/useSearchFilter";
import { useAuth } from "../hooks/useAuth";
import { useTableColumns } from "../hooks/useTableColumns";
import { logger } from "helpers/logger";

export default function PendingTaskPageRemastred() {
	//#region Hooks / Services
	const { success: ToastSuccess, error: ToastError} = useToast();
	const [t] = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const hasDataInStore = useRef(false);
	const keycloakAuth = useAuth();
	//#endregion

	//#region State / Store
	const user = keycloakAuth.user;
	const [recordsPerPage, setRecordsPerPage] = useState(10);

	const { data: PendingTaskData, loading, error } = useSelector((state) => ({ data: state.pendingTasks?.data?.data, loading: state.pendingTasks?.loading, error: state.pendingTasks?.error }));

	// Pagination
	const [currentPage, setCurrentPage] = useState(() => {
		const savedPage = sessionStorage.getItem("pendingTasksPageIndex");
		return savedPage ? parseInt(savedPage, 10) : 1;
	});

	// Sort and Filters
	const [filters, setFilters] = useState({
		searchTerm: "",
		startDate: "",
		endDate: "",
	});

	const [sort, setSort] = useState({ field: "created_at", direction: "desc" });

	// Track deleted items locally
	const [deletedRecordIds, setDeletedRecordIds] = useState(new Set());

	// Sort config
	const [sortConfig] = useState({ key: "created_at", direction: "desc" });

	// Loading / background-refresh flags
	const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);
	const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);

	// Delete confirmation dialog
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [selectedRecord, setSelectedRecord] = useState(null);
	//#endregion

	//#region Derived Values
	// Derive total count and pages from PendingTaskData
	const totalCount = PendingTaskData?.totalCount;

	const totalPages = Math.max(1, Math.ceil((totalCount || 0) / recordsPerPage));

	// Display data: use PendingTaskData directly, filter out locally deleted records
	const displayData = useMemo(() => {
		return PendingTaskData?.tasks?.filter((record) => !deletedRecordIds.has(record.processInstanceId));
	}, [PendingTaskData, deletedRecordIds]);
	//#endregion

	//#region FILTERS & SORT
	const { setSearchQuery } = useSearchFilter(displayData, ["generatedEmanifestID", "name", "taskName", "assignee", "taskId", "processInstanceId"], null);

	const applyFilters = () => {
		setSearchQuery(filters.searchTerm?.trim() || "");
		setCurrentPage(1);

		dispatch(
			fetchPendingTasksRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: recordsPerPage,
				searchParam: filters.searchTerm?.trim() || "",
				startDate: toDDMMYYYY(filters.startDate),
				endDate: toDDMMYYYY(filters.endDate),
				sortField: sort.field,
				sortDirection: sort.direction,
			})
		);
	};

	const clearFilters = () => {
		const cleared = { searchTerm: "", startDate: "", endDate: "" };
		setFilters(cleared);
		setSearchQuery("");
		setCurrentPage(1);

		dispatch(
			fetchPendingTasksRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: recordsPerPage,
				searchParam: "",
				startDate: "",
				endDate: "",
				sortField: sort.field,
				sortDirection: sort.direction,
			})
		);
	};

	const handleSort = (column, direction) => {
		if (!column) return;

		const nextDirection = direction ?? (column === sort.field ? (sort.direction === "asc" ? "desc" : "asc") : "asc");

		const nextSort = { field: column, direction: nextDirection };
		setSort(nextSort);
		setCurrentPage(1);

		dispatch(
			fetchPendingTasksRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: recordsPerPage,
				searchParam: filters.searchTerm?.trim() || "",
				startDate: toDDMMYYYY(filters.startDate),
				endDate: toDDMMYYYY(filters.endDate),
				sortField: nextSort.field,
				sortDirection: nextSort.direction,
			})
		);
	};

	const handleChangeRecordsPerPage = (e) => {
		const value = Number(e.target.value) || 10;
		setRecordsPerPage(value);
		setCurrentPage(1);

		// fetch immediately with the fresh value (keeps your current structure)
		dispatch(
			fetchPendingTasksRequest({
				assignee: user.id,
				pageNumber: 1,
				recordsByPage: recordsPerPage,
				searchParam: filters.searchTerm?.trim() || "",
				startDate: toDDMMYYYY(filters.startDate),
				endDate: toDDMMYYYY(filters.endDate),
				sortField: sort.field,
				sortDirection: sort.direction,
			})
		);
	};
	//#endregion

	//#region Helper Functions
	const toDDMMYYYY = (isoLike) => {
		if (!isoLike) return "";
		const [y, m, d] = isoLike.split("-");
		if (!y || !m || !d) return "";
		return `${d}-${m}-${y}`;
	};

	const parseIfJson = (data) => {
		try {
			const v = JSON.parse(data);
			return typeof v === "string" ? parseIfJson(v) : v;
		} catch {
			return data;
		}
	};

	const GetRender = useCallback((renderId) => {
		const a = document.createElement("a");
		a.href = renderId;
		a.target = "_blank";
		a.download = "";
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	}, []);
	//#endregion

	//#region FETCH PROCESS
	useEffect(() => {
		(async () => {
			dispatch(
				fetchPendingTasksRequest({
					assignee: user.id,
					pageNumber: currentPage,
					recordsByPage: recordsPerPage,
					searchParam: filters.searchTerm?.trim() || "",
					startDate: toDDMMYYYY(filters.startDate),
					endDate: toDDMMYYYY(filters.endDate),
					sortField: sort.field,
					sortDirection: sort.direction,
				})
			);

			sessionStorage.removeItem("pendingTasksPageIndex");
		})();
	}, [dispatch, user.id, currentPage, recordsPerPage]);

	useEffect(() => {
		const hasExistingData = displayData?.length > 0;

		if (!loading && isBackgroundRefreshing) {
			setIsBackgroundRefreshing(false);
			setDeletedRecordIds(new Set()); // Clear local deletions after refresh
		}

		if (hasExistingData) {
			hasDataInStore.current = hasExistingData;
		}

		if (loading && hasExistingData) {
			setIsBackgroundRefreshing(true);
		}
	}, [loading, isBackgroundRefreshing, displayData]);
	//#endregion

	//#region VIEW PROCESS
	const handleViewSummary = useCallback(
		async (row) => {
			const formData = row?.formData || {};
			if (!formData.hyperLinkVariables) return;

			try {
				setIsLoadingBackdrop(true);

				const dataHyperLink = JSON.parse(formData.hyperLinkVariables);

				const formResult = await dispatch(
					fetchFormByID({
						form_key: dataHyperLink.formKey,
						pageNumber: null,
						formData: dataHyperLink,
						readonly: true,
						startingPage: 1,
					})
				);

				if (!fetchFormByID.fulfilled.match(formResult)) {
					logger.error("fetchFormByID failed:", formResult.payload);
					setIsLoadingBackdrop(false);
					return;
				}

				const form = formResult.payload;
				const parsedFormData = {
					...JSON.parse(form.data.formData),
				};

				if (parsedFormData.settings && parsedFormData.settings.data) {
					const parsedSettings = Object.fromEntries(Object.entries(parsedFormData.settings.data).map(([key, value]) => [key, parseIfJson(value)]));
					parsedFormData.settings.data = parsedSettings;
				}

				sessionStorage.setItem("pendingTasksPageIndex", String(currentPage));
				setIsLoadingBackdrop(false);
				navigate("/workflow-history-view", { state: { data: parsedFormData } });
			} catch (error) {
				logger.error("handleViewSummary error:", error);
				setIsLoadingBackdrop(false);
			}
		},
		[dispatch, currentPage, navigate]
	);
	//#endregion

	// Cache for parsed records to avoid repeated JSON parsing
	const parsedRecordsCache = useRef(new Map());

	const handleExecute = useCallback(
		async (row) => {
			try {
				setIsLoadingBackdrop(true);

				const formData = structuredClone(row?.formData || {});

				if (!formData.executionId) {
					formData.executionId =
						row?.executionId ?? row?.formData?.executionId;
				}
				if (formData?.settings?.data && typeof formData.settings.data === "object") {
					const normalizedSettingsData = Object.fromEntries(Object.entries(formData.settings.data).map(([key, value]) => [key, parseIfJson(value)]));

					formData.settings = {
						...formData.settings,
						data: normalizedSettingsData.processVariables,
					};
				}

				setIsLoadingBackdrop(false);

				navigate("/form_view_renderer", {
					state: { data: formData },
				});
			} catch (err) {
				logger.error("handleExecute failed:", err);
				setIsLoadingBackdrop(false);
			}
		},
		[navigate]
);
	//#endregion

	//#region UNCLAIM PROCESS
	const handleUnclaim = useCallback(
		async (taskId) => {
			try {
				setIsLoadingBackdrop(true);

				const result = await dispatch(
					unclaimPendingTaskThunk({
						taskId: taskId,
						userId: user.id,
					})
				);

				if (unclaimPendingTaskThunk.fulfilled.match(result) && result.payload?.data?.status === 1) {
					ToastSuccess(t("Task unclaimed successfully"));

					// Background refresh
					try {
						dispatch(
							fetchPendingTasksRequest({
								assignee: user.id,
								pageNumber: currentPage,
								recordsByPage: recordsPerPage,
								searchParam: filters.searchTerm?.trim() || "",
								startDate: toDDMMYYYY(filters.startDate),
								endDate: toDDMMYYYY(filters.endDate),
								sortField: sort.field,
								sortDirection: sort.direction,
							})
						);
					} catch (err) {
						logger.error("Failed to refresh pending tasks after unclaim:", err);
					}
				} else {
					ToastError(t("Failed to unclaim task"));
				}
			} catch (error) {
				logger.error("Error unclaiming task:", error);
				ToastError(t("Failed to unclaim task"));
			} finally {
				setIsLoadingBackdrop(false);
			}
		},
		[dispatch, user.id, t, ToastError, currentPage, recordsPerPage]
	);
	//#endregion

	//#region DELETE PROCESS
	const handleDeleteClick = useCallback((row) => {
		setSelectedRecord(row);
		setDeleteConfirmOpen(true);
	}, []);

	const handleCancelDelete = () => {
		setDeleteConfirmOpen(false);
		setSelectedRecord(null);
	};

	const handleDelete = useCallback(
		async (processInstanceId, tableName, createdId) => {
			try {
				const result = await dispatch(deletePendingTaskThunk({ rootProcessInstanceId: processInstanceId }));

				if (deletePendingTaskThunk.fulfilled.match(result) && result.payload?.data?.status === 1) {
					// Optimistically remove from UI
					setDeletedRecordIds((prev) => {
						const next = new Set(prev);
						next.add(processInstanceId);
						return next;
					});

					// Patch DB record if needed
					if (createdId != null && createdId !== "") {
						const JsonToPatch = {
							tableName: tableName,
							fields: {
								taskStatus: "Deleted"
							},
							matchField: "id",
							matchValue: createdId
						};

						const patchResult = await dispatch(patchDatabaseRecord({ JsonToPatch }));

						if (!patchDatabaseRecord.fulfilled.match(patchResult)) {
							logger.error("Failed to update taskStatus after deletion");
						}
					}

					ToastSuccess(t(em.deleted_successfully));

					// Background refresh after delete
					try {
						dispatch(
							fetchPendingTasksRequest({
								assignee: user.id,
								pageNumber: currentPage,
								recordsByPage: recordsPerPage,
								searchParam: filters.searchTerm?.trim() || "",
								startDate: toDDMMYYYY(filters.startDate),
								endDate: toDDMMYYYY(filters.endDate),
								sortField: sort.field,
								sortDirection: sort.direction,
							})
						);
					} catch (err) {
						logger.error("Failed to refresh pending tasks after delete:", err);
					}
				} else {
					ToastError(t(em.failed_delete_workflow));
				}
			} catch (error) {
				logger.error("Error removing task:", error);
				ToastError(t(em.failed_delete_workflow));
			}
		},
		[dispatch, t, ToastError, user.id, currentPage, recordsPerPage]
	);

	const handleConfirmDelete = () => {
		if (!selectedRecord) return;

		handleDelete(selectedRecord.processInstanceId, selectedRecord.processDefinition?.key, selectedRecord?.createdId);

		setDeleteConfirmOpen(false);
		setSelectedRecord(null);
	};
	//#endregion

	//#region DOWNLOAD PROCESS
	const downloadPdf = useCallback(
		async (processInstanceId, table_name, tableCreatedId) => {
			try {
				const variablesResult = await dispatch(fetchTaskVariables({ processInstanceId }));

				const variables = variablesResult.payload?.data || [];

				var result = variables.reduce((result, item) => {
					result[item.name] = item.value;
					return result;
				}, {});

				if (jQuery.isEmptyObject(result)) {
					const recordResult = await dispatch(
						fetchDatabaseRecord({
							tableName: table_name,
							fieldName: "id",
							itemId: tableCreatedId,
						})
					);

					if (fetchDatabaseRecord.fulfilled.match(recordResult)) {
						const rec = recordResult?.payload?.data?.record;
						if (rec?.renderId) {
							GetRender(rec.renderId);
						}
					}
				}
			} catch (err) {
				logger.error("Failed to get task variables:", err);
			}
		},
		[dispatch, GetRender]
	);
	//#endregion

	//#region PENDING TASKS TABLE COLUMNS
	const actions = useMemo(() => ({
		onExecute: handleExecute,
		onViewSummary: handleViewSummary,
		onDownloadPdf: (row) => downloadPdf(row?.formData?.renderId),
		onDelete: handleDeleteClick,
		onUnclaim: (row) => handleUnclaim(row.processInstanceId),
	}), [handleExecute, handleViewSummary, downloadPdf, handleDeleteClick, handleUnclaim]);

	const { columns } = useTableColumns('PendingTasks', { actions });
	//#endregion

	//#region RETURN COMPONENT
	if (loading && !hasDataInStore.current) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
				<Loader variant="dots" message="Loading." />
			</Box>
		);
	}

	if (error && displayData?.length === 0) {
		return <Alert severity="error">{error}</Alert>;
	}

	return (
		<Box sx={{ p: 3 }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Typography variant="h3" color="primary">
					{t("Pending Tasks")}
				</Typography>

				{isBackgroundRefreshing && (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<Loader variant="circular" message="Loading." size="small" />
					</Box>
				)}
			</Box>

			<Typography variant="body1" color="text.secondary" paragraph>
				{t("Manage your assigned tasks and track their progress.")}
			</Typography>

			<Divider sx={{ my: 3 }} />

			{/* Filters */}
			<Box
				sx={{
					my: 2,
					p: 2,
					borderRadius: 2,
					bgcolor: "rgba(29,131,84,0.04)",
					display: "flex",
					flexWrap: "wrap",
					alignItems: "center",
					gap: 2,
				}}
			>
				{/* Search */}
				<Box sx={{ flex: 1, minWidth: 260 }}>
					<SearchFilterBar
						/* Backend search & filtering */
						mode="backend"
						/* Search input */
						searchValue={filters.searchTerm}
						onSearchChange={(v) => setFilters((prev) => ({ ...prev, searchTerm: v }))}
						/* Date range filters */
						showDateRange
						startDateValue={filters.startDate}
						endDateValue={filters.endDate}
						onStartDateChange={(v) => setFilters((prev) => ({ ...prev, startDate: v }))}
						onEndDateChange={(v) => setFilters((prev) => ({ ...prev, endDate: v }))}
						/* Apply / Clear actions */
						showActions
						onApply={applyFilters}
						onClear={clearFilters}
						/* Disable unused UI elements */
						showFilterButton={false}
						showSearchButton={false}
					/>
				</Box>
			</Box>

			<Typography variant="body1">
				{t("Total Pending Tasks")}: {totalCount || 0}
			</Typography>

			{!PendingTaskData || displayData?.length === 0 ? (
				<DataContainer data={[]} emptyStateIcon={CalendarIcon} emptyStateTitle="No Pending Tasks yet" emptyStateSubtitle="Wait for someone to assign a task for you" />
			) : (
				<Box sx={{ mt: 2 }}>
					<DataContainer data={displayData} columns={columns} onSort={handleSort} sortConfig={sortConfig} showRowNumbers={true} maxHeight={600} stickyHeader={true} />
				</Box>
			)}

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<TextField
					select
					size="small"
					label={t("Per page")}
					value={recordsPerPage}
					onChange={handleChangeRecordsPerPage}
					sx={{
						minWidth: 120,
						"& .MuiOutlinedInput-root": {
							"& fieldset": { borderColor: "primary.main" },
							"&:hover fieldset": { borderColor: "primary.main" },
							"&.Mui-focused fieldset": { borderColor: "primary.main" },
						},
					}}
				>
					{[5, 10, 15].map((n) => (
						<MenuItem key={n} value={n}>
							{n}
						</MenuItem>
					))}
				</TextField>

				<CustomPagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={(event, value) => setCurrentPage(value)}
					color="primary"
					showFirstButton
					showLastButton
					sx={{ mt: 2 }}
				/>
			</Box>

			<EnhancedConfirmationDialog
				open={deleteConfirmOpen}
				onClose={() => {
					setDeleteConfirmOpen(false);
					setSelectedRecord(null);
				}}
				onConfirm={handleConfirmDelete}
				title={t("Confirm Delete")}
				message={t(em.areyousuredelete)}
				confirmText={t("DELETE")}
				confirmColor="error"
				cancelText={t("Cancel")}
				showMessage={true}
			/>

			<LoadingBackdrop open={isLoadingBackdrop} message="Loading data, please wait..." variant="bars" color="greenGradient" />
		</Box>
	);

	// #endregion
}
